import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import useSeo from "@/hooks/useSeo";
import { site } from "@/mocks/site";
import ContactForm from "./components/ContactForm";

const levels = [
  {
    label: "已证实",
    icon: "ri-checkbox-circle-line",
    cls: "border-secondary-300 bg-secondary-50 text-secondary-700",
    body: "有明确发布主体、可追溯发布时间，内容可被第三方核验。例如工作室声明的存在、平台对某类内容的处理结果。",
    example: "示例：工作室于 8 月 29 日发布文字声明。",
  },
  {
    label: "待确认",
    icon: "ri-error-warning-line",
    cls: "border-accent-300 bg-accent-50 text-accent-700",
    body: "来源不明、无法追溯首发者，或内容内部存在矛盾。它既不能被确认，也不宜被当成结论传播。",
    example: "示例：标注「内部人士提供」的聊天记录截图。",
  },
  {
    label: "已被辟谣",
    icon: "ri-close-circle-line",
    cls: "border-primary-300 bg-primary-50 text-primary-700",
    body: "经时间戳比对、行程核对或来源追溯后，可以判定其不成立。我们会说明判定依据，而不是只给结论。",
    example: "示例：时间戳与公开直播时间冲突的对话截图。",
  },
];

const principles = [
  { t: "只整理公开信息", d: "不采用无法追溯来源的材料，不替任何一方发布单方面爆料。" },
  { t: "结论与依据一起给", d: "每一条判断都说明是依据来源、时间戳还是交叉核对得出的。" },
  { t: "明确说出不知道", d: "无法确认时标注「待确认」，而不是用推测把空白填满。" },
  { t: "持续更新，保留修订记录", d: "事件进展会同步进时间线，旧文若有修正会在文内说明。" },
  { t: "不参与情绪对线", d: "不使用煽动性标题，不对当事人作人身评价。" },
];

const voices = [
  {
    name: "读者 · 阿澈",
    role: "从短视频来的路人",
    text: "之前刷到的都是三十秒切片，看完这家才发现事情有时间线。至少现在我分得清哪些是聊天记录、哪些是官方声明了。",
  },
  {
    name: "读者 · 小满",
    role: "关注事件两周",
    text: "最喜欢信息分级这一块，把「待确认」单独标出来很有用。不站队但把方法讲清楚，比两边互骂的帖子有用多了。",
  },
  {
    name: "读者 · 木子",
    role: "做内容编辑",
    text: "声明逐句解读那篇写得实在，把「保留追究权利」这类表述讲明白了，我拿去当资料参考了。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于本站｜灵灵事件信息梳理站的立场与信息分级",
    description:
      "了解灵灵事件信息梳理站的定位、信息分级标准与内容原则：只整理公开信息、结论与依据一起给、明确说出不知道，并提供联系与线索投稿方式。",
    keywords: "灵灵事件梳理站,信息分级,内容原则,辟谣方法,线索投稿",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于本站 - 灵灵事件信息梳理站",
        description: site.desc,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系与线索投稿",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: site.name,
        email: site.email,
        description: "中立整理灵灵争议相关公开信息，标注信息可信等级。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页" },
          { "@type": "ListItem", position: 2, name: "关于本站" },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: "关于本站" }]} />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-eye-line" aria-hidden="true"></i>
                {site.name}
              </span>
              <h1 className="mt-5 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                不替谁说话，只把公开信息整理清楚
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                {site.desc}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">一句话定位</h2>
                <p className="mt-3 font-heading text-lg leading-relaxed text-primary-700">
                  {site.tagline}
                </p>
                <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-background-200 pt-5 text-center">
                  <div>
                    <dt className="text-xs text-foreground-500">栏目</dt>
                    <dd className="mt-1 font-heading text-xl font-bold text-foreground-900">4</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">信息等级</dt>
                    <dd className="mt-1 font-heading text-xl font-bold text-foreground-900">3</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">立场</dt>
                    <dd className="mt-1 font-heading text-xl font-bold text-foreground-900">中立</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-14">
          <SectionHeading
            eyebrow="信息分级标准"
            title="三个等级，对应三种信任成本"
            desc="等级只描述信息的可核验程度，不代表对任何一方的定性。"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {levels.map((lv) => (
              <article key={lv.label} className={`rounded-lg border p-5 ${lv.cls}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50/70">
                  <i className={`${lv.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{lv.label}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-700 md:text-sm">{lv.body}</p>
                <p className="mt-3 border-t border-background-300 pt-3 text-xs leading-relaxed text-foreground-600">
                  {lv.example}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto grid max-w-content grid-cols-1 gap-8 px-4 py-12 md:px-6 md:py-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <div className="mb-2 flex items-center gap-2">
              <span className="h-px w-6 bg-accent-500" aria-hidden="true"></span>
              <span className="text-xs font-medium tracking-widest text-accent-700">内容原则</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              我们给自己定的五条规矩
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              争议事件里，表达欲是最廉价的东西。以下五条决定了我们会做什么、以及更重要的——不做什么。
            </p>
          </div>
          <ol className="space-y-4 lg:col-span-7">
            {principles.map((p, i) => (
              <li key={p.t} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                  {i + 1}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground-900 md:text-base">{p.t}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-foreground-600 md:text-sm">{p.d}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-14">
          <SectionHeading eyebrow="读者反馈" title="他们是这样用这个站的" />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {voices.map((v) => (
              <figure key={v.name} className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5">
                <i className="ri-double-quotes-l text-2xl text-primary-400" aria-hidden="true"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {v.text}
                </blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-3">
                  <span className="block text-sm font-semibold text-foreground-900">{v.name}</span>
                  <span className="block text-xs text-foreground-500">{v.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-5">
              <div className="mb-2 flex items-center gap-2">
                <span className="h-px w-6 bg-primary-500" aria-hidden="true"></span>
                <span className="text-xs font-medium tracking-widest text-primary-600">联系我们</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                纠错、补充来源或提问
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你手上有更准确的时间戳、原始发布记录，或者发现文中表述有误，欢迎告诉我们。请尽量附上可核验的线索。
              </p>
              <ul className="mt-6 space-y-4">
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  <a href={`mailto:${site.email}`} className="cursor-pointer text-sm text-foreground-800 hover:text-primary-700">
                    {site.email}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-time-line" aria-hidden="true"></i>
                  </span>
                  <span className="text-sm text-foreground-700">工作日 10:00 - 18:00 集中处理来信</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-article-line" aria-hidden="true"></i>
                  </span>
                  <span className="text-sm leading-relaxed text-foreground-700">
                    本站不设线下办公地点，也不接受任何形式的商业推广投放。
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}